import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ReviewCard } from "@/components/ReviewCard";
import { MyBadges } from "@/components/Badges";
import { MarketingConsentToggle } from "@/components/MarketingConsentToggle";
import { ProfileEditor } from "@/components/ProfileEditor";
import { toast } from "sonner";

export default function PersonalArea() {
  const { user, loading, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const loadReviews = async () => {
    try {
      const { data } = await api.get("/reviews/me");
      setReviews(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (user) loadReviews(); }, [user]);

  const handleDeleteReview = async (rid) => {
    if (!window.confirm("Vuoi eliminare questa esperienza?")) return;
    try {
      await api.delete(`/reviews/${rid}`);
      toast.success("Esperienza eliminata");
      loadReviews();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      toast.success("Account eliminato");
    } catch { toast.error("Errore nell'eliminazione dell'account"); }
  };

  if (loading || !user) {
    return <div className="max-w-7xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="personal-area">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="md:col-span-3">
          <div className="card-surface p-6 sticky top-24">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-[#E8E6E1]" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#DCE4DC] flex items-center justify-center font-display text-xl text-[#1A362D]">{(user.name || "?")[0]}</div>
              )}
              <div className="min-w-0">
                <p className="font-display text-lg text-[#1A362D] truncate">{user.name}</p>
                <p className="font-ui text-xs text-[#8A9691] truncate">{user.email}</p>
              </div>
            </div>
            <div className="mt-6 divider pt-6 space-y-2">
              {user.username_slug && (
                <Link to={`/utente/${user.username_slug}`} data-testid="my-public-profile-link" className="btn-secondary w-full text-sm text-center block">
                  Il mio profilo pubblico
                </Link>
              )}
              <button onClick={logout} data-testid="logout-btn" className="btn-secondary w-full text-sm">Esci</button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="md:col-span-9 space-y-12">
          <ProfileEditor />
          <MyBadges />
          <MarketingConsentToggle />

          <section>
            <h1 className="font-display text-4xl text-[#1A362D] tracking-tight">Le mie esperienze</h1>
            <p className="mt-2 font-ui text-sm text-[#5C6B64]">Gestisci le esperienze che hai lasciato. Puoi eliminarle in qualsiasi momento.</p>

            <div className="mt-6 space-y-4">
              {reviews.length === 0 ? (
                <div className="card-surface p-10 text-center">
                  <p className="font-display text-2xl text-[#1A362D]">Non hai ancora raccontato nessuna esperienza</p>
                  <p className="mt-2 font-ui text-sm text-[#5C6B64]">Inizia esplorando le scuole della tua zona.</p>
                  <Link to="/cerca" data-testid="go-search-btn" className="inline-block mt-4 btn-primary text-sm">Cerca scuole</Link>
                </div>
              ) : (
                reviews.map((r) => (
                  <div key={r.id}>
                    {r.school && (
                      <Link to={`/scuola/${r.school.id}`} data-testid={`my-review-school-link-${r.id}`} className="block font-ui text-xs text-[#C05C45] uppercase tracking-wider mb-1 hover:underline">
                        {r.school.name} — {r.school.city}
                      </Link>
                    )}
                    <ReviewCard review={r} onDelete={handleDeleteReview} />
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Danger zone */}
          <section className="card-surface p-8 border-[#B33A3A]/30">
            <p className="label-eyebrow text-[#B33A3A]">Area pericolosa</p>
            <h2 className="mt-2 font-display text-2xl text-[#1A362D]">Elimina il mio account</h2>
            <p className="mt-2 font-ui text-sm text-[#5C6B64] leading-relaxed max-w-xl">
              Verranno cancellati definitivamente il tuo profilo e tutte le tue esperienze. Questa azione non è reversibile.
            </p>
            {confirmDelete ? (
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button onClick={handleDeleteAccount} data-testid="confirm-delete-account-btn" className="btn-danger">Confermo: elimina tutto</button>
                <button onClick={() => setConfirmDelete(false)} data-testid="cancel-delete-account-btn" className="btn-secondary">Annulla</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} data-testid="delete-account-btn" className="mt-4 btn-danger">Elimina account ed esperienze</button>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
